import { z } from 'zod';
import { AiUsageLedgerDimensionsSchema } from './ai-usage';

export const AI_LOG_LIVE_QUEUE_NAMES = ['ignite-ai-logs-staging', 'ignite-ai-logs'] as const;
export type AiLogLiveQueueName = (typeof AI_LOG_LIVE_QUEUE_NAMES)[number];

export const AI_LOG_DLQ_QUEUE_NAMES = ['ignite-ai-logs-dlq-staging', 'ignite-ai-logs-dlq'] as const;
export type AiLogDlqQueueName = (typeof AI_LOG_DLQ_QUEUE_NAMES)[number];

export const AI_LOG_DLQ_TO_LIVE_QUEUE = {
  'ignite-ai-logs-dlq-staging': 'ignite-ai-logs-staging',
  'ignite-ai-logs-dlq': 'ignite-ai-logs',
} as const satisfies Record<AiLogDlqQueueName, AiLogLiveQueueName>;

const AiObservabilityReplayMetadata = {
  failed_job_id: z.number().int().positive().optional(),
};

export const AiLogWorker = z.enum(['api', 'analyzer']);
export const AiAttemptResult = z.enum(['success', 'failure']);
export const AiAvailabilityErrorCategory = z.enum([
  'configuration',
  'authentication',
  'rate_limit',
  'timeout',
  'upstream',
  'invalid_response',
  'unsupported_media',
  'internal',
]);
export const AiOutputQualityResult = z.enum(['pending', 'passed', 'failed', 'not_evaluated']);
export const AiResponseFormatMode = z.enum(['none', 'json_schema']);

export const AiAttemptPointerEvent = z.object({
  kind: z.literal('ai_attempt_completed.v1'),
  event_id: z.string().uuid(),
  attempt_id: z.string().uuid(),
  execution_id: z.string().min(1).max(200),
  call_id: z.string().min(1).max(200).nullable().default(null),
  action_type: z.string().min(1).max(80).nullable().default(null),
  action_id: z.string().min(1).max(200).nullable().default(null),
  message_id: z.string().min(1).max(200).nullable().default(null),
  internal_call: z.boolean().default(false),
  attempt_number: z.number().int().positive(),
  occurred_at: z.string().datetime(),
  authority_committed_at: z.string().datetime().nullable().default(null),
  worker: AiLogWorker,
  feature_key: z.string().min(1).max(80).nullable(),
  task_type: z.string().min(1).max(80),
  prompt_version_id: z.string().min(1).max(128).nullable(),
  input_contract_version: z.string().min(1).max(64).nullable(),
  output_contract_version: z.string().min(1).max(64).nullable(),
  response_format_mode: AiResponseFormatMode,
  vision_used: z.boolean(),
  tenant_id: z.string().min(1).max(80),
  user_id: z.string().min(1).max(80).nullable(),
  platform: z.string().min(1).max(40).nullable(),
  product_model_id: z.string().min(1).max(120),
  runtime_provider: z.string().min(1).max(80),
  runtime_model_id: z.string().min(1).max(240),
  gateway_config_id: z.string().min(1).max(120),
  provider_request_id: z.string().max(240).nullable().default(null),
  result: AiAttemptResult,
  error_category: AiAvailabilityErrorCategory.nullable(),
  error_code: z.string().max(120).nullable(),
  error_message: z.string().max(1000).nullable(),
  http_status: z.number().int().min(100).max(599).nullable(),
  latency_ms: z.number().int().nonnegative(),
  prompt_tokens: z.number().int().nonnegative().nullable(),
  completion_tokens: z.number().int().nonnegative().nullable(),
  cache_write_tokens: z.number().int().nonnegative().nullable().default(null),
  cache_read_tokens: z.number().int().nonnegative().nullable().default(null),
  reasoning_tokens: z.number().int().nonnegative().nullable().default(null),
  total_tokens: z.number().int().nonnegative().nullable().default(null),
  input_cost_per_1m_cents: z.number().int().nonnegative().nullable().default(null),
  output_cost_per_1m_cents: z.number().int().nonnegative().nullable().default(null),
  actual_cost_micros: z.number().int().nonnegative().nullable().default(null),
  currency: z.string().length(3).nullable().default(null),
  billing_owner: z.enum(['user_points', 'platform']).nullable().default(null),
  reservation_id: z.string().max(80).nullable().default(null),
  skill_id: z.string().max(128).nullable().default(null),
  tool_call_id: z.string().max(200).nullable().default(null),
  tool_name: z.string().max(120).nullable().default(null),
  usage_dimensions: AiUsageLedgerDimensionsSchema.default({
    output_units: null,
    input_tokens: null,
    output_tokens: null,
    cached_tokens: null,
    reasoning_tokens: null,
    total_tokens: null,
    image_input_units: null,
    image_output_units: null,
    audio_input_units: null,
    audio_output_units: null,
    tool_calls: null,
  }),
  fixed_points: z.number().int().nonnegative().nullable().default(null),
  output_quality: AiOutputQualityResult,
  payload_r2_key: z.string().min(1).max(1024),
  payload_size_bytes: z.number().int().nonnegative(),
  payload_status: z.enum(['pending', 'available', 'missing', 'deleted']).default('available'),
  ...AiObservabilityReplayMetadata,
});

export const AiQualityEvent = z.object({
  kind: z.literal('ai_attempt_quality_evaluated.v1'),
  event_id: z.string().uuid(),
  attempt_id: z.string().uuid(),
  tenant_id: z.string().min(1).max(80),
  occurred_at: z.string().datetime(),
  result: z.enum(['passed', 'failed']),
  category: z.string().max(80).nullable(),
  code: z.string().max(120).nullable(),
  ...AiObservabilityReplayMetadata,
});

export const AiLogCleanupEvent = z.object({
  kind: z.literal('ai_log_cleanup.v1'),
  event_id: z.string().uuid(),
  job_id: z.string().uuid(),
  from: z.string().datetime(),
  to: z.string().datetime(),
  requested_by: z.string().min(1).max(80),
  reason: z.string().min(1).max(500),
  ...AiObservabilityReplayMetadata,
});

const AdminAiLogCleanupTimestamp = z.string().datetime({ offset: true });

export const AdminAiLogCleanupStatus = z.enum(['pending', 'running', 'completed', 'failed']);
export type AdminAiLogCleanupStatus = z.infer<typeof AdminAiLogCleanupStatus>;

export const AdminAiLogCleanupRequest = z
  .object({
    from: AdminAiLogCleanupTimestamp,
    to: AdminAiLogCleanupTimestamp,
    reason: z.string().trim().min(1).max(500),
  })
  .strict()
  .refine((value) => Date.parse(value.from) < Date.parse(value.to), {
    path: ['to'],
    message: '`from` must be earlier than `to`',
  });
export type AdminAiLogCleanupRequest = z.infer<typeof AdminAiLogCleanupRequest>;

export const AdminAiLogCleanupAccepted = z
  .object({
    id: z.string().uuid(),
    status: z.literal('pending'),
  })
  .strict();
export type AdminAiLogCleanupAccepted = z.infer<typeof AdminAiLogCleanupAccepted>;

export const AdminAiLogCleanupJob = z
  .object({
    id: z.string().uuid(),
    from_at: AdminAiLogCleanupTimestamp,
    to_at: AdminAiLogCleanupTimestamp,
    reason: z.string().min(1).max(500),
    status: AdminAiLogCleanupStatus,
    deleted_count: z.number().int().nonnegative(),
    failed_count: z.number().int().nonnegative(),
    last_error: z.string().max(1000).nullable(),
    created_at: AdminAiLogCleanupTimestamp,
    updated_at: AdminAiLogCleanupTimestamp,
    completed_at: AdminAiLogCleanupTimestamp.nullable(),
  })
  .strict();
export type AdminAiLogCleanupJob = z.infer<typeof AdminAiLogCleanupJob>;

export const GenerationRequestStatus = z.enum([
  'running',
  'succeeded',
  'partially_succeeded',
  'failed',
]);
export const GenerationRunStatus = z.enum(['ready', 'blocked', 'failed']);
export const GenerationFailureStage = z.enum([
  'request_validation',
  'routing',
  'source_resolution',
  'quota_accounting',
  'prompt_loading',
  'model_call',
  'output_validation',
  'safety_validation',
  'persistence',
  'internal',
]);
export type GenerationFailureStage = z.infer<typeof GenerationFailureStage>;

export const GenerationRequestCompletedEvent = z.object({
  kind: z.literal('generation_request_completed.v1'),
  event_id: z.string().uuid(),
  request_id: z.string().uuid(),
  occurred_at: z.string().datetime(),
  started_at: z.string().datetime(),
  route: z.string().min(1).max(120),
  tenant_id: z.string().min(1).max(80),
  user_id: z.string().min(1).max(80),
  platform: z.string().min(1).max(40).nullable(),
  topic_count: z.number().int().positive(),
  variant_count: z.number().int().positive(),
  status: GenerationRequestStatus,
  failed_stage: GenerationFailureStage.nullable(),
  error_code: z.string().max(120).nullable(),
  error_message: z.string().max(500).nullable(),
  run_count: z.number().int().nonnegative(),
  model_attempt_count: z.number().int().nonnegative(),
  ...AiObservabilityReplayMetadata,
});

export const GenerationRunCompletedEvent = z.object({
  kind: z.literal('generation_run_completed.v1'),
  event_id: z.string().uuid(),
  generation_id: z.string().uuid(),
  request_id: z.string().uuid(),
  occurred_at: z.string().datetime(),
  variant_index: z.number().int().positive(),
  topic_index: z.number().int().positive().nullable(),
  status: GenerationRunStatus,
  failed_stage: GenerationFailureStage.nullable(),
  error_code: z.string().max(120).nullable(),
  error_message: z.string().max(500).nullable(),
  model_attempt_count: z.number().int().nonnegative(),
  draft_id: z.string().nullable(),
  ...AiObservabilityReplayMetadata,
});

export const AiObservabilityQueueEvent = z.discriminatedUnion('kind', [
  AiAttemptPointerEvent,
  AiQualityEvent,
  AiLogCleanupEvent,
  GenerationRequestCompletedEvent,
  GenerationRunCompletedEvent,
]);

export type AiAttemptPointerEvent = z.infer<typeof AiAttemptPointerEvent>;
export type AiQualityEvent = z.infer<typeof AiQualityEvent>;
export type AiLogCleanupEvent = z.infer<typeof AiLogCleanupEvent>;
export type GenerationRequestCompletedEvent = z.infer<typeof GenerationRequestCompletedEvent>;
export type GenerationRunCompletedEvent = z.infer<typeof GenerationRunCompletedEvent>;
export type AiObservabilityQueueEvent = z.infer<typeof AiObservabilityQueueEvent>;
export type AiObservabilityDomainEvent = AiObservabilityQueueEvent extends infer Event
  ? Event extends unknown
    ? Omit<Event, 'failed_job_id'>
    : never
  : never;

export function withoutAiObservabilityReplayMetadata(
  event: AiObservabilityQueueEvent,
): AiObservabilityDomainEvent {
  const { failed_job_id: _failedJobId, ...domainEvent } = event;
  return domainEvent;
}

export const AiAttemptPayload = z.object({
  schema_version: z.literal(2),
  request: z.object({
    messages: z.array(
      z
        .object({
          role: z.enum(['system', 'user', 'assistant']),
          content: z.union([
            z.string(),
            z.array(
              z.discriminatedUnion('type', [
                z.object({ type: z.literal('text'), text: z.string() }).strict(),
                z
                  .object({
                    type: z.literal('image_url'),
                    image_url: z
                      .object({
                        url: z.string().min(1).max(2_000_000),
                        detail: z.enum(['auto', 'low', 'high']).optional(),
                      })
                      .strict(),
                  })
                  .strict(),
                z
                  .object({
                    type: z.literal('input_audio'),
                    input_audio: z
                      .object({ data: z.string().min(1), format: z.string().min(1) })
                      .strict(),
                  })
                  .strict(),
              ]),
            ),
          ]),
        })
        .strict(),
    ),
    max_tokens: z.number().int().positive(),
    temperature: z.number(),
    cache_key: z.string().nullable(),
    tags: z.array(z.string()),
    response_format: z
      .object({
        type: z.literal('json_schema'),
        name: z.string().min(1).max(64),
        schema: z.record(z.string(), z.unknown()),
        strict: z.boolean(),
      })
      .strict()
      .nullable(),
  }),
  response: z
    .object({ raw_body: z.unknown(), headers: z.record(z.string(), z.string()).nullable() })
    .nullable(),
});
export type AiAttemptPayload = z.infer<typeof AiAttemptPayload>;

export interface AdminAiRequestLogItem {
  attempt_id: string;
  execution_id: string;
  attempt_number: number;
  occurred_at: string;
  worker: 'api' | 'analyzer';
  feature_key: string | null;
  task_type: string;
  prompt_version_id: string | null;
  input_contract_version: string | null;
  output_contract_version: string | null;
  response_format_mode: 'none' | 'json_schema';
  vision_used: boolean;
  tenant_id: string;
  user_id: string | null;
  platform: string | null;
  product_model_id: string;
  runtime_provider: string;
  runtime_model_id: string;
  provider_request_id: string | null;
  result: 'success' | 'failure';
  error_category: string | null;
  error_code: string | null;
  error_message: string | null;
  http_status: number | null;
  latency_ms: number;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  cache_write_tokens: number | null;
  cache_read_tokens: number | null;
  reasoning_tokens: number | null;
  total_tokens: number | null;
  input_cost_per_1m_cents: number | null;
  output_cost_per_1m_cents: number | null;
  actual_cost_micros: number | null;
  currency: string | null;
  billing_owner: 'user_points' | 'platform' | null;
  reservation_id: string | null;
  fixed_points: number | null;
  payload_status: 'available' | 'deleted' | 'missing';
  payload_deleted_at: string | null;
  output_quality: 'pending' | 'passed' | 'failed' | 'not_evaluated';
}

export interface AdminAiRequestLogsResponse {
  items: AdminAiRequestLogItem[];
  next_cursor: string | null;
}

export interface AdminAiRequestLogDetail extends AdminAiRequestLogItem {
  payload: AiAttemptPayload | null;
}

export interface AdminAiModelHealthItem {
  product_model_id: string;
  runtime_provider: string;
  runtime_model_id: string;
  availability: { sample_size: number; failure_count: number; failure_rate: number; state: string };
  output_quality: {
    sample_size: number;
    failure_count: number;
    failure_rate: number;
    state: string;
  };
}

export interface AdminAiModelHealthResponse {
  window_hours: 24;
  items: AdminAiModelHealthItem[];
}

export interface AdminGenerationRunLog {
  generation_id: string;
  request_id: string;
  occurred_at: string;
  variant_index: number;
  topic_index: number | null;
  status: 'ready' | 'blocked' | 'failed';
  failed_stage: string | null;
  error_code: string | null;
  error_message: string | null;
  model_attempt_count: number;
  draft_id: string | null;
}

export interface AdminGenerationRequestLog {
  request_id: string;
  occurred_at: string;
  started_at: string;
  route: string;
  tenant_id: string;
  user_id: string;
  platform: string | null;
  topic_count: number;
  variant_count: number;
  status: 'running' | 'succeeded' | 'partially_succeeded' | 'failed';
  failed_stage: string | null;
  error_code: string | null;
  error_message: string | null;
  run_count: number;
  model_attempt_count: number;
}

export interface AdminGenerationRequestLogsResponse {
  items: AdminGenerationRequestLog[];
  next_cursor: string | null;
}

export interface AdminGenerationRequestLogDetail extends AdminGenerationRequestLog {
  runs: AdminGenerationRunLog[];
  attempts: AdminAiRequestLogItem[];
}
